import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { regenerateReading, type ReadingRecord } from '@/api/client';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Priestess-only "Regenerate this reading" button.
 *
 * Free models (Llama 3.3 / Nemotron / etc.) occasionally produce
 * truncated or duplicated output on long spreads. Subscribers pay for
 * an unlimited experience, so we let them re-roll the AI text without
 * redrawing cards. The button is invisible to non-subscribers — they
 * don't get this perk, and exposing it would just frustrate them.
 *
 * Usage: pass the reading id (from `reading.id`) and an onUpdated
 * callback that swaps the interpretation in the parent's state.
 */

interface Props {
  readingId: string;
  onUpdated: (reading: ReadingRecord) => void;
  className?: string;
}

// Feature flag — temporarily hidden across the app.
// Reason: current model (Gemini Flash-Lite) returns full readings
// reliably, so the manual re-roll is no longer worth the AI cost.
// Backend endpoint and i18n strings stay intact — flip this to false
// to bring the button back instantly.
const REGENERATE_HIDDEN = true;

export default function RegenerateButton({ readingId, onUpdated, className }: Props) {
  const { t } = useTranslation();
  const { isPriestess } = useSubscription();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (REGENERATE_HIDDEN) return null;
  if (!isPriestess) return null;

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await regenerateReading(readingId);
      onUpdated(updated);
      // Scroll to top of interpretation so the user sees the new version
      // from the start, not stuck mid-page.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`text-center ${className ?? ''}`}>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full border
                   border-gold-500/40 bg-night-900/50 px-5 py-2 text-sm
                   text-gold-200 shadow-[0_0_10px_rgba(232,195,86,0.18)]
                   transition active:scale-95 hover:border-gold-400
                   hover:text-gold-100 disabled:opacity-50"
      >
        {busy ? (
          <>⌛ {t('regenerate.busy', { defaultValue: 'Перегенерируем…' })}</>
        ) : (
          <>↻ {t('regenerate.button', { defaultValue: 'Перегенерировать' })}</>
        )}
      </button>
      <p className="mt-1 text-[10px] uppercase tracking-widest text-gold-400/60">
        ✦ {t('regenerate.hint', { defaultValue: 'Привилегия «Жрицы»' })}
      </p>
      {error && (
        <p className="mt-2 text-xs text-rose-400">{error}</p>
      )}
    </div>
  );
}
